<template>
  <div class="bg-white h-full flex flex-col w-full p-4 overflow-y-auto">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center h-full">
      <loader />
      <span class="mt-4 text-gray-500">טוען את פרטי התשלום...</span>
    </div>

    <!-- Booking slot lapsed and was taken: the payment is refused before charging -->
    <div v-else-if="slotUnavailable" class="flex flex-col items-center justify-center h-full gap-4 text-center px-4">
      <i class="pi pi-calendar-times text-5xl text-red-500" />
      <span class="text-lg font-medium text-gray-700">השעה שנשמרה עבורכם כבר נתפסה</span>
      <span class="text-sm text-gray-500">לא בוצע חיוב. אנא קבעו תור חדש 🙏</span>
    </div>

    <!-- Already Paid State -->
    <div v-else-if="alreadyPaid && alreadyPaidResponse" class="flex flex-col items-center justify-center h-full ">
      <div class="flex-grow flex flex-col items-center justify-center text-center">
        <LottieAnimation
          style="width: 35%;"
          ref="anim"
          :animationData="paymentSuccess"
          :autoPlay="true"
          :loop="false"
          :speed="1" />

        <h2 class="text-center font-medium text-2xl mb-1 mt-4">התשלום הושלם בהצלחה</h2>
        <span class="text-center text-sm font-light">תשלום על סך {{ alreadyPaidResponse.price }}₪ הושלם בהצלחה</span>
      </div>

      <div class="bg-white rounded-xl w-full border border-gray-300 flex-col flex-start mb-12" style="padding:2rem;">
        <h2 class="text-xl font-medium gap-2 flex items-center mb-4">
          <i class="pi pi-credit-card" />
          <span>פרטי התשלום</span>
        </h2>
        <div class="flex flex-col gap-3 text-sm">
          <div class="flex justify-between w-full">
            <span class="text-gray-500 w-1/2">תאריך ביצוע</span>
            <span
              class="text-left">{{ DateTime.fromISO(alreadyPaidResponse.date).toLocaleString(DateTime.DATETIME_MED)
              }}</span>
          </div>
          <div class="flex justify-between w-full">
            <span class="text-gray-500 w-1/2">אמצעי תשלום</span>
            <span class="text-left">{{ paymentMethodsNames[alreadyPaidResponse.paymentMethod] }}</span>
          </div>
          <div class="flex justify-between w-full">
            <span class="text-gray-500 w-1/2">מספר עסקה</span>
            <span class="text-left">{{ alreadyPaidResponse.transactionNumber }}</span>
          </div>
          <div class="flex justify-between w-full">
            <span class="text-gray-500 w-1/2">סכום</span>
            <span class="text-left">₪{{ alreadyPaidResponse.price }}</span>
          </div>
          <div class="flex justify-between w-full">
            <span class="text-gray-500 w-1/2">סטטוס</span>
            <span class="text-left text-green-500 flex gap-1 items-center px-2 rounded-lg bg-green-50">
              <span>שולם</span>
              <i class="pi pi-check" style="font-size: 0.75rem" />
            </span>
          </div>
        </div>
        <div
          class="flex justify-between items-center text-sm p-2 w-full rounded-lg bg-gray-50 mt-6 text-gray-500 gap-3">
          <i class="pi pi-exclamation-circle" />
          <span>אישור עסקה וחשבונית נשלחו אליכם לכתובת האימייל שהזנתם</span>
        </div>
      </div>
    </div>

    <!-- Card Saved State (before-appointment / save-card flows) -->
    <div v-else-if="cardSaved" class="flex flex-col items-center justify-center h-full">
      <div class="flex-grow flex flex-col items-center justify-center text-center">
        <LottieAnimation
          style="width: 35%;"
          ref="anim"
          :animationData="paymentSuccess"
          :autoPlay="true"
          :loop="false"
          :speed="1" />

        <h2 class="text-center font-medium text-2xl mb-1 mt-4">הכרטיס נשמר בהצלחה</h2>
        <span class="text-center text-sm font-light">
          {{ isProductOrder
            ? 'ההזמנה התקבלה! העסק יחייב את הכרטיס עד גובה ההזמנה'
            : sessionData?.chargeMode === 'before-appointment'
              ? `התור אושר! החיוב יתבצע אוטומטית ב-${formatChargeDate(savedScheduledChargeDate)}`
              : 'התור אושר! העסק יחייב את הכרטיס בהתאם למדיניות' }}
        </span>
      </div>
    </div>

    <!-- Payment Form -->
    <div v-else class="flex flex-col h-full">

      <!-- Payment Summary -->
      <div class="bg-white rounded-xl mb-4 border border-gray-300 flex-col flex-start" style="padding:2rem;">
        <h2 class="text-xl font-medium gap-2 flex items-center mb-4">
          <i class="pi pi-shopping-bag" />
          <span>ההזמנה שלכם</span>
        </h2>
        <div class="flex flex-col gap-2">
          <div v-for="item in sessionData?.saleData?.items" class="flex justify-between items-center">
            <span>{{ item.name }}</span>
            <span>{{ item.price > 0 ? `₪${item.price.toFixed(1)}` :  `₪${(item.price * -1).toFixed(1)}-`}}</span>
          </div>
        </div>
        <divider />
        <div class="flex justify-between items-center text-lg">
          <span>סה״כ לתשלום</span>
          <span>₪{{ sessionData?.saleData?.total.amount.value }}</span>
        </div>
      </div>

      <!-- Card Details Form -->
      <div class="bg-white">
        <h3 class="font-medium text-2xl mb-4">פרטי תשלום</h3>
        <!-- Email (required by PayMe) -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            אימייל
          </label>
          <input
            v-model="email"
            type="email"
            dir="ltr"
            required
            placeholder="example@email.com"
            class="w-full border border-gray-300 rounded-lg p-3 h-12"
            :class="{'border-red-500': emailError}"
          />
          <span v-if="emailError" class="text-red-500 text-xs mt-1">{{ emailError }}</span>
        </div>

        <!-- Phone (only when the payment carries no phone of its own — e.g. a payment form
             created through the external API. PayMe's tokenizer requires one.) -->
        <div v-if="needsPhone" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            טלפון
          </label>
          <input
            v-model="phone"
            type="tel"
            dir="ltr"
            required
            placeholder="050-0000000"
            class="w-full border border-gray-300 rounded-lg p-3 h-12"
            :class="{'border-red-500': phoneError}"
          />
          <span v-if="phoneError" class="text-red-500 text-xs mt-1">{{ phoneError }}</span>
        </div>

        <!-- Card Number -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">מספר כרטיס</label>
          <div class="relative">
            <div
              id="card-number-field"
              class="w-full border border-gray-300 rounded-lg p-3 h-12 flex items-center justify-center"
              :class="{'border-red-500': fieldErrors.cardNumber, 'pl-14': cardBrand}"
            >
              <span v-if="!fieldsReady" class="text-gray-400 text-sm">טוען...</span>
            </div>
          </div>
          <span v-if="fieldErrors.cardNumber" class="text-red-500 text-xs mt-1">{{ fieldErrors?.cardNumber }}</span>
        </div>

        <!-- Expiry and CVV Row -->
        <div class="flex gap-4 mb-4">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-1">תוקף</label>
            <div
              id="card-expiry-field"
              class="w-full border border-gray-300 rounded-lg p-3 h-12 flex items-center justify-center"
              :class="{'border-red-500': fieldErrors.expiry}"
            >
              <span v-if="!fieldsReady" class="text-gray-400 text-sm">טוען...</span>
            </div>
            <span v-if="fieldErrors.expiry" class="text-red-500 text-xs mt-1">{{ fieldErrors?.expiry }}</span>
          </div>
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-1">CVV</label>
            <div
              id="card-cvv-field"
              class="w-full border border-gray-300 rounded-lg p-3 h-12 flex items-center justify-center"
              :class="{'border-red-500': fieldErrors.cvv}"
            >
              <span v-if="!fieldsReady" class="text-gray-400 text-sm">טוען...</span>
            </div>
            <span v-if="fieldErrors.cvv" class="text-red-500 text-xs mt-1">{{ fieldErrors.cvv }}</span>
          </div>
        </div>

        <!-- installments -->
        <div v-if="installmentOptions.length > 1" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">תשלומים</label>
          <Select
            v-model="installments"
            placeholder="כמה תשלומים?"
            :options="installmentOptions"
            class="w-full"
            optionLabel="name"
            optionValue="value"
          />
        </div>
      </div>

      <!-- Delayed-charge / save-card consent -->
      <div v-if="isSaveCardFlow" class="text-sm text-gray-600 bg-blue-50 p-3 rounded border border-blue-200 mb-3">
        <i class="pi pi-info-circle text-blue-600 ml-1"></i>
        {{ sessionData?.chargeMode === 'before-appointment'
          ? `הכרטיס לא יחויב עכשיו — החיוב יתבצע אוטומטית ב-${formatChargeDate(sessionData?.scheduledChargeDate)}`
          : isProductOrder
            ? `הכרטיס לא יחויב עכשיו — פרטי הכרטיס יישמרו והעסק יחייב עד ₪${sessionData?.saleData?.total?.amount?.value} עבור ההזמנה`
            : `הכרטיס לא יחויב עכשיו — פרטי הכרטיס יישמרו והעסק רשאי לחייב עד ₪${sessionData?.saleData?.total?.amount?.value} בהתאם למדיניות הביטולים` }}
      </div>

      <div v-if="sessionData?.cancellationPolicyText"
           class="text-sm text-gray-600 bg-gray-50 p-3 rounded border border-gray-200 mb-3">
        <div class="font-medium mb-1">מדיניות ביטולים</div>
        <div class="whitespace-pre-line">{{ sessionData.cancellationPolicyText }}</div>
      </div>

      <label v-if="isSaveCardFlow" class="flex items-start gap-2 text-sm text-gray-700 mb-2 cursor-pointer">
        <input v-model="policyConsent" type="checkbox" class="mt-1" />
        <span>
          {{ sessionData?.chargeMode === 'before-appointment'
            ? 'אני מאשר/ת את מדיניות הביטולים ואת חיוב הכרטיס באופן אוטומטי לפני מועד התור'
            : isProductOrder
              ? 'אני מאשר/ת את שמירת פרטי הכרטיס לחיוב על ידי העסק עד גובה ההזמנה'
              : 'אני מאשר/ת את מדיניות הביטולים ואת שמירת פרטי הכרטיס לחיוב על ידי העסק עד גובה ההזמנה' }}
        </span>
      </label>

      <Button
        :disabled="isProcessing || !fieldsReady || (isSaveCardFlow && !policyConsent)"
        :loading="isProcessing || !fieldsReady"
        class="w-full mt-2 flex-none text-white font-bold rounded-xl text-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        @click="processPayment"
        :label="isProcessing ? 'מעבד תשלום...' : !fieldsReady ? 'טוען שדות...' : isSaveCardFlow ? (isProductOrder ? 'אישור הזמנה ושמירת כרטיס' : 'אישור תור ושמירת כרטיס') : 'תשלום'"
      />

      <divider v-if="walletVisible && sessionData?.saleData?.paymentMethods?.bit">
        או
      </divider>

      <div class="flex gap-4 flex-col" v-if="walletVisible">
        <!--        <Button-->
        <!--          :disabled="isProcessing || !fieldsReady"-->
        <!--          class="w-full flex items-center justify-center gap-2 !py-0 !px-2"-->
        <!--          @click=""-->
        <!--          outlined-->
        <!--          plain-->
        <!--        >-->
        <!--          <span>תשלום באמצעות</span>-->
        <!--          <img src="../assets/apple-pay.png" alt="" class="w-12 h-12">-->
        <!--        </Button>-->

        <!--        <Button-->
        <!--          :disabled="isProcessing || !fieldsReady"-->
        <!--          class="w-full flex items-center justify-center gap-2 !py-0 !px-2"-->
        <!--          @click=""-->
        <!--          outlined-->
        <!--          plain-->
        <!--        >-->
        <!--          <span>תשלום באמצעות</span>-->
        <!--          <img src="../assets/google-pay.png" alt="" class="w-12 h-12">-->
        <!--        </Button>-->

        <Button
          :disabled="isProcessing || !fieldsReady"
          class="w-full flex items-center justify-center gap-2"
          style="padding: 1.25rem"
          @click="payWithBit"
          outlined
          :loading="bitButtonLoading"
          plain
          v-if="sessionData?.saleData?.paymentMethods?.bit"
        >
          <template v-if="!bitButtonLoading">
            <span>תשלום באמצעות</span>
            <img src="../../assets/bit-logo.png" alt="" class="w-7 h-7" style="width:1.75rem; height: 1.75rem; ">
          </template>
          <template v-else>
            <loader />
          </template>
        </Button>
      </div>

      <p class="text-center text-xs text-gray-500 mt-auto p-4 pt-6">
        <i class="pi pi-shield" />
        התשלום מאובטח באמצעות <a href="https://payme.io" target="_blank">PayMe</a>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ClientApi } from '../api/client-api';
import { IPaymeSessionResponse, PaymePaymentMethods } from '@torim-app/types';
import Select from 'primevue/select';
import Button from 'primevue/button';
import Loader from '../components/Loader.vue';
import { LottieAnimation } from 'lottie-web-vue';
import paymentSuccessData from '../assets/payment_success.json';
import { DateTime } from 'luxon';
import { useToast } from 'vue-toastification';
import { validateInputPhone } from '@torim-app/utils';

// ===== TypeScript Type Definitions for PayMe SDK =====
interface PayMeSDK {
  create(apiKey: string, options: PayMeCreateOptions): Promise<PayMeInstance>;
}

interface PayMeCreateOptions {
  testMode?: boolean;
  language?: string;
  tokenIsPermanent?: boolean;
}

interface PayMeInstance {
  hostedFields(): PayMeHostedFields;
  tokenize(saleData: any): Promise<any>;
  destroy?(): void;
}

interface PayMeHostedFields {
  create(type: string, config?: any): PayMeField;
}

interface PayMeField {
  mount(elementId: string): Promise<void>;
  on(event: string, callback: (data: any) => void): void;
}

// Extend the Window interface to include PayMe
declare global {
  interface Window {
    PayMe?: PayMeSDK;
  }
}

// Error type for better error handling
interface PaymentError {
  message?: string;
  payload?: {
    status_error_details:string;
  }
  [key: string]: any;
}

// ===== End of Type Definitions =====

const paymentSuccess = paymentSuccessData;
const props = defineProps({
  showWallet: {
    default: true,
    required: false,
    type: Boolean
  },
  paymentUuid: {
    type: String,
    required: false
  },
  // Staff-run (card-present) charges offer installments up to this many, even when the
  // business hasn't enabled them for customer payment links.
  maxInstallments: {
    type: Number,
    required: false
  }
});

const emit = defineEmits(['completed']);

const route = useRoute();
const router = useRouter();
const toast = useToast();

const paymentRequestUuid = props.paymentUuid ?? route.params.pruid as string;

// The server turns wallets off for a card-present charge (sessionData.showWallet === false).
const walletVisible = computed(() => props.showWallet && sessionData.value?.showWallet !== false);

const isLoading = ref(true);
const isProcessing = ref(false);
const fieldsReady = ref(false);
const alreadyPaid = ref(false);
const slotUnavailable = ref(false);
const alreadyPaidResponse = ref<{
  transactionNumber: string;
  price: number;
  paymentMethod: string;
  date: string;
}>();
const sessionData = ref<IPaymeSessionResponse>();
const email = ref('');
// Only collected when the payment request has no phone attached (API-created forms).
const phone = ref('');
const phoneError = ref('');
const installments = ref<number>(1);
const installmentOptions = ref<{ name: string; value: number }[]>([]);
const emailError = ref('');
const cardBrand = ref<string>('');
const fieldErrors = ref<{ cardNumber?: string; expiry?: string; cvv?: string }>({
  cvv: '',
  cardNumber: '',
  expiry: ''
});
const bitButtonLoading = ref<boolean>(false);
// Delayed-charge flows: the card is tokenized and saved instead of charged immediately.
const policyConsent = ref(false);
const cardSaved = ref(false);
const savedScheduledChargeDate = ref<string | undefined>();

// PayMe's hosted-fields tokenizer rejects a missing payerPhone the same way it rejects a
// missing payerEmail, so a payment with no phone on file has to ask the payer for one.
const needsPhone = computed(() => !sessionData.value?.saleData?.payerPhone);
const payerPhone = computed(() => sessionData.value?.saleData?.payerPhone || phone.value.trim());

const isProductOrder = computed(() => sessionData.value?.purpose === 'product-order');
const isSaveCardFlow = computed(() =>
  sessionData.value?.chargeMode === 'before-appointment' || sessionData.value?.chargeMode === 'save-card'
);

const formatChargeDate = (date?: string): string =>
  date ? DateTime.fromISO(date).setLocale('he').toLocaleString(DateTime.DATETIME_MED) : '';

const paymentMethodsNames: Record<string, string> = {
  'apple-pay': 'Apple Pay',
  'bank-transfer': 'העברה בנקאית',
  'google-pay': 'Google Pay',
  'pay-box': 'Paybox',
  bit: 'ביט',
  cash: 'מזומן',
  credit: 'כרטיס אשראי',
  'credit-card': 'כרטיס אשראי'
};

let paymeInstance: PayMeInstance | null = null;
let hostedFields: any = null;

const loadPaymeScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.PayMe) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.payme.io/hf/v1/hostedfields.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load PayMe script'));
    document.head.appendChild(script);
  });
};

const initPayment = async () => {
  isLoading.value = true;
  fieldsReady.value = false;

  try {
    // Get session data from backend
    sessionData.value = await ClientApi.getPaymeSession(paymentRequestUuid);

    // Set email if provided
    if (sessionData.value?.saleData.payerEmail) {
      email.value = sessionData.value.saleData.payerEmail;
    }

    // A card that's only saved (charged later) never takes installments
    const chargesNow = !sessionData.value.chargeMode || sessionData.value.chargeMode === 'immediate';
    const maxInstallments = chargesNow ? Math.min(12, props.maxInstallments ?? sessionData.value.installments ?? 1) : 1;
    if (maxInstallments > 1) {
      installmentOptions.value = Array.from({ length: maxInstallments }, (_, i) => ({
        name: i === 0 ? 'תשלום אחד' : `${i + 1} תשלומים`,
        value: i + 1
      }));
    }

    // Load PayMe script
    await loadPaymeScript();

    // Initialize PayMe hosted fields
    const apiKey = sessionData.value?.sellerKey;
    const testMode = sessionData.value?.testMode;

    console.log('Initializing PayMe with:', { apiKey, testMode });

    if (!window.PayMe) {
      throw new Error('PayMe SDK not loaded');
    }

    paymeInstance = await window.PayMe.create(apiKey, {
      testMode,
      language: 'he',
      // Delayed-charge flows need a permanent token the backend can charge later.
      tokenIsPermanent: isSaveCardFlow.value
    });

    console.log('PayMe instance created');

    // Get hosted fields manager
    const fields = paymeInstance.hostedFields();

    console.log('Hosted fields manager obtained');

    // Turn off loading BEFORE mounting fields so DOM is rendered
    isLoading.value = false;

    // Wait for next tick to ensure DOM is ready after isLoading changed
    await nextTick();

    // Define field styles to reuse
    const fieldStyles = {
      base: {
        color: '#000',
        fontSize: '16px',
        fontFamily: 'Heebo, sans-serif',
        '::placeholder': {
          color: '#9CA3AF'
        }
      },
      invalid: {
        color: '#EF4444'
      }
    };

    // Create and mount individual fields
    const cardNumberField = fields.create('cardNumber', {
      placeholder: '0000 0000 0000 0000',
      styles: fieldStyles
    });
    cardNumberField.mount('#card-number-field');

    const expiryField = fields.create('cardExpiration', {
      placeholder: 'MM/YY',
      styles: fieldStyles
    });
    expiryField.mount('#card-expiry-field');

    const cvcField = fields.create('cvc', {
      placeholder: 'CVV',
      styles: fieldStyles
    });
    cvcField.mount('#card-cvv-field');

    console.log('Fields mounted successfully');

    // Listen to field events
    cardNumberField.on('validity-changed', (event: any) => {
      console.log('Card number validity:', event);
      if (!event.isValid && event.message) {
        fieldErrors.value.cardNumber = event.message;
      } else {
        delete fieldErrors.value.cardNumber;
      }
    });

    cardNumberField.on('brand-changed', (event: any) => {
      console.log('Card brand:', event);
      cardBrand.value = event.brand || '';
    });

    expiryField.on('validity-changed', (event: any) => {
      console.log('Expiry validity:', event);
      if (!event.isValid && event.message) {
        fieldErrors.value.expiry = event.message;
      } else {
        delete fieldErrors.value.expiry;
      }
    });

    cvcField.on('validity-changed', (event: any) => {
      console.log('CVC validity:', event);
      if (!event.isValid && event.message) {
        fieldErrors.value.cvv = event.message;
      } else {
        delete fieldErrors.value.cvv;
      }
    });

    fieldsReady.value = true;
    console.log('All fields ready');
  } catch (e) {
    const err = e as PaymentError;
    console.error('PayMe initialization error:', err);
    isLoading.value = false;
    fieldsReady.value = false;

    if (err?.message === 'already-paid') {
      alreadyPaid.value = true;
      alreadyPaidResponse.value = err as any;
    } else if (err?.message === 'slot-unavailable') {
      slotUnavailable.value = true;
    } else {
      console.error('PayMe initialization error:', err);
      toast.error('אירעה שגיאה בטעינת טופס התשלום');
    }
  }
};

const processPayment = async () => {
  if (!paymeInstance || !sessionData.value) {
    return;
  }

  // Validate email
  emailError.value = '';
  if (!email.value || !email.value.includes('@')) {
    emailError.value = 'נדרש אימייל תקין';
    return;
  }

  phoneError.value = '';
  if (needsPhone.value && !validateInputPhone(phone.value)) {
    phoneError.value = 'נדרש מספר טלפון תקין';
    return;
  }

  isProcessing.value = true;
  fieldErrors.value = {};

  try {
    console.log('=== Starting tokenization ===');
    console.log('PayMe instance exists:', !!paymeInstance);
    console.log('Session data:', sessionData.value);
    console.log('Email from input:', email.value);

    // Prepare tokenization data according to PayMe docs
    // Note: Card data (number, expiry, cvc) are already captured in hosted fields
    // We only need to provide payer info and sale details
    const saleData = {
      payerFirstName: sessionData.value.saleData.payerFirstName,
      payerLastName: sessionData.value.saleData.payerLastName,
      payerEmail: email.value, // Use email from input
      // From the payment request when it has one, otherwise from the input above —
      // PayMe's validator makes both payerEmail and payerPhone mandatory.
      payerPhone: payerPhone.value,
      total: {
        label: sessionData.value.saleData.total.label,
        amount: {
          currency: sessionData.value.saleData.total.amount.currency,
          value: sessionData.value.saleData.total.amount.value
        }
      }
    };

    console.log('Sale data for tokenization:', JSON.stringify(saleData, null, 2));
    console.log('Calling paymeInstance.tokenize()...');

    // Create a timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Tokenization timeout after 30 seconds'));
      }, 30000);
    });

    // Race between tokenization and timeout
    const tokenResult: any = await Promise.race([
      paymeInstance.tokenize(saleData),
      timeoutPromise
    ]).catch((err) => {
      console.error('Tokenization promise rejected:', err);
      throw err;
    });

    console.log('=== Tokenization completed ===');
    console.log('Token result:', tokenResult);
    console.log('Token result type:', tokenResult?.type);

    // Check for errors
    if (tokenResult.type === 'tokenize-error') {
      console.error('Tokenization error:', tokenResult);

      // Handle validation errors
      if (tokenResult.validationError && tokenResult.errors) {
        console.log('Validation errors:', tokenResult.errors);
        const errors = tokenResult.errors;

        if (errors.payerEmail) {
          toast.error(`שגיאה באימייל: ${errors.payerEmail}`);
        } else if (errors.payerPhone) {
          toast.error(`שגיאה בטלפון: ${errors.payerPhone}`);
        } else if (errors.payerFirstName) {
          toast.error(`שגיאה בשם פרטי: ${errors.payerFirstName}`);
        } else if (errors.payerLastName) {
          toast.error(`שגיאה בשם משפחה: ${errors.payerLastName}`);
        } else {
          toast.error('אנא תקן את השגיאות בטופס');
        }
        isProcessing.value = false;
        return;
      }

      throw new Error(tokenResult.message || tokenResult.error || 'שגיאה בעיבוד פרטי הכרטיס');
    }

    // Check if we got a token
    if (!tokenResult.token) {
      console.error('No token in result:', tokenResult);
      throw new Error('לא התקבל אסימון תשלום');
    }

    console.log('Token received successfully:', tokenResult.token);
    console.log('Sending to backend...');

    // Delayed-charge flows: save the card instead of charging it (no sale is generated now).
    if (isSaveCardFlow.value) {
      const saveResult = await ClientApi.savePaymeCard({
        paymentRequestUuid: sessionData.value.paymentRequestUuid,
        token: tokenResult.token,
        email: email.value,
        consent: policyConsent.value
      });

      if (saveResult.success) {
        savedScheduledChargeDate.value = saveResult.scheduledChargeDate ?? sessionData.value.scheduledChargeDate;
        cardSaved.value = true;
        setTimeout(() => emit('completed'), 2000);
      } else {
        toast.error('שמירת הכרטיס נכשלה, אנא נסה שוב');
        isProcessing.value = false;
      }
      return;
    }

    // Send token to backend to generate sale
    const result = await ClientApi.generatePaymeSale({
      paymentRequestUuid: sessionData.value.paymentRequestUuid,
      paymentMethod: PaymePaymentMethods.CreditCard,
      token: tokenResult.token,
      email: email.value,
      installments: installments.value ?? 1

    });

    console.log('Backend response:', result);

    if (result.success) {
      console.log('Payment successful, redirecting...');
      alreadyPaidResponse.value = {
        date: result.date as string,
        price: result.price,
        paymentMethod: result.paymentMethod,
        transactionNumber: result.transactionId
      };
      alreadyPaid.value = true;
      setTimeout(() => emit('completed'), 2000);
    } else {
     toast.error('התשלום נכשל, אנא נסה שוב')
      isProcessing.value = false;
    }
  } catch (e) {
    const err = e as PaymentError;
    console.error('=== Payment processing error ===');
    console.error('Error:', err);
    console.error('Error message:', err?.message);

    isProcessing.value = false;
    if (err?.message === 'slot-unavailable') {
      slotUnavailable.value = true;
      return;
    }
    toast.error(err?.payload?.status_error_details ?? 'אירעה שגיאה בטעינת טופס התשלום');
  }
};

const payWithBit = async () => {
  try {
    if (!paymeInstance || !sessionData.value) {
      return;
    }

    if (email.value === '' || !email.value.includes('@')) {
      emailError.value = 'חובה להזין כתובת אימייל תקינה';
      return;
    }

    phoneError.value = '';
    if (needsPhone.value && !validateInputPhone(phone.value)) {
      phoneError.value = 'נדרש מספר טלפון תקין';
      return;
    }

    bitButtonLoading.value = true;
    const result = await ClientApi.generatePaymeSale({
      paymentRequestUuid: sessionData.value.paymentRequestUuid,
      paymentMethod: PaymePaymentMethods.Bit,
      email: email.value
    });

    if (result.success && result.saleUrl)
      window.open(result.saleUrl, '_blank')?.focus();

  } catch (e) {
    const err = e as PaymentError;
    toast.error(err?.message || 'אירעה שגיאה בעיבוד התשלום');
    isProcessing.value = false;

  } finally {
    bitButtonLoading.value = false;
  }
};

onMounted(() => {
  initPayment();
});

onUnmounted(() => {
  // Cleanup PayMe instance if needed
  if (paymeInstance) {
    try {
      paymeInstance.destroy?.();
    } catch (e) {
      console.error('Cleanup error:', e);
    }
  }
});
</script>

<style scoped>

#card-number-field,
#card-expiry-field,
#card-cvv-field {
  background-color: white;
  position: relative;
}

/* Ensure the iframe from PayMe fits properly and overlays any loading text */
#card-number-field iframe,
#card-expiry-field iframe,
#card-cvv-field iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}
</style>
